'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import ReportsHeader from './components/ReportsHeader';
import BillableHoursReport from './components/BillableHoursReport';
import UtilizationTrends from './components/UtilizationTrends';
import CredentialComplianceReport from './components/CredentialComplianceReport';
import ExportHistory from './components/ExportHistory';
import { reportsService, type ReportExport } from '@/lib/services/staffingService';

export type ReportsTab = 'billable_hours' | 'utilization' | 'credentials' | 'history';

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState<ReportsTab>('billable_hours');
  const [exportHistory, setExportHistory] = useState<ReportExport[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const loadHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const data = await reportsService.getExportHistory();
      setExportHistory(data);
    } catch {
      // silent — history is non-critical
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleExportLogged = (entry: ReportExport) => {
    setExportHistory((prev) => [entry, ...prev]);
  };

  return (
    <AppLayout>
      <div className="flex flex-col h-full">
        <ReportsHeader activeTab={activeTab} onTabChange={setActiveTab} />
        <div className="flex-1 overflow-auto px-6 pb-6">
          {activeTab === 'billable_hours' && (
            <BillableHoursReport onExportLogged={handleExportLogged} />
          )}
          {activeTab === 'utilization' && (
            <UtilizationTrends onExportLogged={handleExportLogged} />
          )}
          {activeTab === 'credentials' && (
            <CredentialComplianceReport onExportLogged={handleExportLogged} />
          )}
          {activeTab === 'history' && (
            <ExportHistory history={exportHistory} loading={historyLoading} />
          )}
        </div>
      </div>
    </AppLayout>
  );
}
